import { GoogleOutlined, LockOutlined, MailOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Divider,
  Form,
  Input,
  Space,
  Spin,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { PATHS } from "../../../app/router/paths";
import { isApiError } from "../../../shared/errors/ApiError";
import { authApi } from "../api/authApi";
import { useAuth } from "../model/AuthContext";
import type { LoginRequest, OAuthProvider } from "../model/types";

type RedirectState = {
  from?: { pathname?: string; search?: string };
};

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  oauth_access_denied: "外部アカウントでのログインがキャンセルされました。",
  oauth_state_invalid:
    "ログイン操作の有効期限が切れました。もう一度お試しください。",
  oauth_provider_failed:
    "外部ログインサービスを確認できませんでした。しばらくしてからもう一度お試しください。",
  oauth_email_required:
    "外部アカウントから確認済みメールアドレスを取得できませんでした。",
  oauth_domain_not_allowed: "このメールドメインではログインできません。",
  oauth_account_conflict:
    "この外部アカウントは別のユーザーに関連付けられています。",
  oauth_user_inactive: "このユーザーは無効になっています。",
  oauth_user_deleted: "このユーザーは削除されています。",
  oauth_app_access_mismatch: "このアカウントは顧客サイトでは利用できません。",
};

export const LoginPage = () => {
  const { status, login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [oauthSubmitting, setOauthSubmitting] = useState<OAuthProvider | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setOauthSubmitting(null);
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const oauthError = new URLSearchParams(location.search).get("oauth_error");
  const displayedError =
    errorMessage ||
    (oauthError
      ? (OAUTH_ERROR_MESSAGES[oauthError] ??
        "外部アカウントでのログインに失敗しました。")
      : null);

  if (status === "loading") {
    return (
      <div
        style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (status === "authenticated") {
    return <Navigate to={PATHS.HOME} replace />;
  }

  const destination = (): string => {
    const from = (location.state as RedirectState | null)?.from;
    return from?.pathname ? `${from.pathname}${from.search ?? ""}` : PATHS.HOME;
  };

  const handleSubmit = async (values: LoginRequest) => {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await login(values);
      navigate(destination(), { replace: true });
    } catch (error) {
      setErrorMessage(
        isApiError(error) ? error.message : "ログインに失敗しました",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleOAuthLogin = (provider: OAuthProvider) => {
    setErrorMessage(null);
    setOauthSubmitting(provider);
    localStorage.setItem("has_session", "true");
    window.location.assign(authApi.oauthStartUrl(provider, destination()));
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        background: "#f5f7fa",
      }}
    >
      <Card style={{ width: "100%", maxWidth: 420 }}>
        <Typography.Title
          level={1}
          style={{ textAlign: "center", marginBottom: 38 }}
        >
          顧客ポータル
        </Typography.Title>

        {displayedError && (
          <Alert
            type="error"
            showIcon
            message={displayedError}
            style={{ marginBottom: 16 }}
          />
        )}

        <Form<LoginRequest>
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
        >
          <Form.Item
            style={{ marginBottom: 28 }}
            label="メールアドレス"
            name="email"
            rules={[
              { required: true, message: "メールアドレスを入力してください" },
            ]}
          >
            <Input
              prefix={<MailOutlined style={{ marginRight: 10 }} />}
              autoComplete="email"
              style={{ height: 42 }}
            />
          </Form.Item>
          <Form.Item
            style={{ marginBottom: 28 }}
            label="パスワード"
            name="password"
            rules={[
              { required: true, message: "パスワードを入力してください" },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ marginRight: 10 }} />}
              autoComplete="current-password"
              style={{ height: 42 }}
            />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={submitting}
            disabled={oauthSubmitting !== null}
            block
            style={{ height: 48, fontSize: 16, fontWeight: "bold" }}
          >
            ログイン
          </Button>
        </Form>

        <Divider plain>または</Divider>
        <Space orientation="vertical" size={12} style={{ width: "100%" }}>
          <Button
            block
            icon={<GoogleOutlined />}
            loading={oauthSubmitting === "google"}
            disabled={
              submitting ||
              (oauthSubmitting !== null && oauthSubmitting !== "google")
            }
            onClick={() => handleOAuthLogin("google")}
            style={{ height: 46 }}
          >
            Google アカウントでログイン
          </Button>
          <Button
            block
            loading={oauthSubmitting === "line"}
            disabled={
              submitting ||
              (oauthSubmitting !== null && oauthSubmitting !== "line")
            }
            onClick={() => handleOAuthLogin("line")}
            style={{
              height: 46,
              color: "#fff",
              background: "#06c755",
              borderColor: "#06c755",
            }}
          >
            LINE アカウントでログイン
          </Button>
        </Space>
      </Card>
    </div>
  );
};
